/**
 * Append-only per-run activity cache (browser localStorage).
 * Keeps screenshot/step timeline visible across tab switches and list polls
 * that return a trimmed progress_log.
 */

export type CachedRunActivity = {
  fingerprint: string;
  timestamp: number;
  kind: string;
  text: string;
  operatorPhase?: string;
  relatedStepId: string;
  screenshots: Array<{
    fingerprint: string;
    url: string;
    thumbnailUrl: string;
    caption: string;
    timestamp: number;
    relatedStepId: string;
  }>;
};

const STORAGE_KEY = "ead-expui.run-activities.v1";

function loadAll(): Record<string, CachedRunActivity[]> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, CachedRunActivity[]>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function saveAll(map: Record<string, CachedRunActivity[]>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota */
  }
}

function activityTextCore(text: string): string {
  return String(text || "")
    .replace(/^\[[^\]]+\]\s*/i, "")
    .replace(/\b(Found|Next|Why|Reasoning):\s*/gi, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function activitySecondBucket(timestamp: number): number {
  return Math.floor(Number(timestamp || 0) / 1000);
}

function screenshotTail(item: CachedRunActivity): string {
  const shot = String(
    item.screenshots?.[0]?.url || item.screenshots?.[0]?.thumbnailUrl || "",
  ).trim();
  if (!shot) return "";
  return shot.split("/").pop() || shot.slice(-48);
}

function isCycleReminderActivity(item: CachedRunActivity): boolean {
  const text = String(item.text || "").toLowerCase();
  return (
    text.includes("[executor pfm delivery reminder") ||
    text.includes("[executor running-phase reminder") ||
    text.includes("pfm delivery saved") ||
    text.includes("[executor] pfm delivery accepted") ||
    text.includes("[executor pfm consolidation delivery")
  );
}

function activitiesSameStep(a: CachedRunActivity, b: CachedRunActivity): boolean {
  if (a.fingerprint && b.fingerprint && a.fingerprint === b.fingerprint) {
    return true;
  }
  if (isCycleReminderActivity(a) || isCycleReminderActivity(b)) {
    return false;
  }
  const bucketA = activitySecondBucket(a.timestamp);
  const bucketB = activitySecondBucket(b.timestamp);
  if (bucketA !== bucketB) return false;
  if (a.relatedStepId !== b.relatedStepId) return false;

  const shotA = screenshotTail(a);
  const shotB = screenshotTail(b);
  if (shotA && shotB) return shotA === shotB;
  // Same second + same tool: text-only refresh of the same activity (keep screenshots).
  if (shotA || shotB) return true;

  const coreA = activityTextCore(a.text);
  const coreB = activityTextCore(b.text);
  if (!coreA || !coreB) return false;
  if (coreA === coreB) return true;
  const probe = coreA.slice(0, 40);
  return probe.length >= 20 && (coreA.includes(coreB.slice(0, 40)) || coreB.includes(probe));
}

function hasUsableScreenshots(
  shots: CachedRunActivity["screenshots"] | undefined,
): boolean {
  return (
    Array.isArray(shots) &&
    shots.some((shot) => String(shot.url || shot.thumbnailUrl || "").trim())
  );
}

function mergeActivityRecords(
  left: CachedRunActivity,
  right: CachedRunActivity,
): CachedRunActivity {
  const preferText =
    String(right.text || "").length >= String(left.text || "").length ? right : left;
  const other = preferText === right ? left : right;
  const rightShots = hasUsableScreenshots(right.screenshots) ? right.screenshots : [];
  const leftShots = hasUsableScreenshots(left.screenshots) ? left.screenshots : [];
  const screenshots = rightShots.length ? rightShots : leftShots;
  return {
    ...other,
    ...preferText,
    text: preferText.text || other.text,
    screenshots,
    operatorPhase: preferText.operatorPhase || other.operatorPhase,
    fingerprint: preferText.fingerprint || other.fingerprint,
  };
}

function upsertActivity(list: CachedRunActivity[], item: CachedRunActivity): CachedRunActivity[] {
  for (let i = 0; i < list.length; i += 1) {
    if (activitiesSameStep(list[i], item)) {
      const next = [...list];
      next[i] = mergeActivityRecords(list[i], item);
      return next;
    }
  }
  return [...list, item];
}

/** Merge incoming activities into the persisted cache; never drop older entries. */
export function mergeRunActivitiesPersisted(
  executionId: string,
  incoming: CachedRunActivity[],
): CachedRunActivity[] {
  const id = String(executionId || "").trim();
  if (!id) return incoming;

  const all = loadAll();
  let merged = [...(all[id] || [])];
  for (const item of incoming) {
    if (!item?.fingerprint) continue;
    merged = upsertActivity(merged, item);
  }
  merged.sort((a, b) => b.timestamp - a.timestamp);
  all[id] = merged;
  saveAll(all);
  return merged;
}

export function loadPersistedRunActivities(executionId: string): CachedRunActivity[] {
  const id = String(executionId || "").trim();
  if (!id) return [];
  return loadAll()[id] || [];
}

export function clearPersistedRunActivities(executionId: string): void {
  const id = String(executionId || "").trim();
  if (!id) return;
  const all = loadAll();
  if (!all[id]) return;
  delete all[id];
  saveAll(all);
}
